import { put, call, select, fork, take } from 'redux-saga/effects'

import {
  mergedSourcesSelector,
  sourcesSelector,
  monthSourcesDataSelector
} from 'reduxModules/selectors/SourceSelectors'
import { routeIdSelector } from 'reduxModules/selectors/RouteSelectors'
import { dateSelector, monthSelector, yearSelector } from 'reduxModules/selectors/DateSelectors'
import { snackbarActions } from 'reduxModules/actions/SnackbarActions'
import { routeActions } from 'reduxModules/actions/RouteActions'
import { sourceTypes } from 'reduxModules/actions/SourceActions'
import { createSourceBooking, handleBooking, handleSourceData, confirmSaga } from 'services/sagaHelpers'
import {
  sortByOrder,
  updateSources,
  createReadableDate,
  getDaysInMonth,
  createDateId,
  createDateFromDateId
} from 'services/helpers'

import { firestore, rsf } from '../../firebase'

export function* getSourcesSagas(action) {
  const { responseFailure, responseSuccess, data: routeId } = action

  try {
    const res = yield call(rsf.firestore.getCollection, `routes/${routeId}/sources`)

    if (!res.empty) {
      const sources = []

      res.forEach(source => sources.push({ id: source.id, ...source.data() }))

      sources.sort(sortByOrder)

      yield put(responseSuccess(sources))
    } else {
      yield put(responseFailure())
    }
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час завантаження систем!'
    }))
  }
}

export function* addSourcesBookingSaga(action) {
  const { responseFailure, responseSuccess, data: { id, label, diff } } = action

  try {
    const bookings = yield createSourceBooking(label, id, diff)

    const res = yield handleBooking(bookings)

    if (res[0]) {
      yield put(snackbarActions.showSnackbar({
        type: 'success',
        message: 'Бронювання успішно добавленно!'
      }))

      yield put(responseSuccess())
    } else {
      yield put(snackbarActions.showSnackbar({
        type: 'error',
        message: 'Сталась помилка під час бронювання, будь ласка повторіть спробу'
      }))

      yield put(responseFailure())
    }
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час добавлення бронювань з систем!'
    }))
  }
}

export function* resetSourceSaga(action) {
  const { responseFailure, responseSuccess, data: systemId } = action

  try {
    const confirmed = yield call(confirmSaga, 'resetSource')

    if (confirmed) {
      const mergedSources = yield select(mergedSourcesSelector)
      const date = yield select(dateSelector)
      const departureDate = createReadableDate(date)
      const routeId = yield select(routeIdSelector)
      const updatedSources = updateSources(mergedSources, systemId, 'booked', 0)

      yield put(routeActions.changeSourceData(updatedSources))

      yield handleSourceData()

      const colRef = yield firestore.collection('bookings')
      const query = colRef
        .where('departureDate', '==', departureDate)
        .where('routeId', '==', routeId)
        .where('sourceId', '==', systemId)

      const res = yield call(rsf.firestore.getCollection, query)

      yield res.forEach(booking => booking.ref.delete())

      yield put(snackbarActions.showSnackbar({
        type: 'success',
        message: 'Система скинута!'
      }))

      yield put(responseSuccess())
    }
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час скидування системи!'
    }))
  }
}

function* unsubscribeMonthSourcesDataSaga(unsubscribe) {
  while (true) {
    yield take(sourceTypes.UNSUBSCRIBE_MONTH_SOURCES_DATA_ATTEMPT)
    unsubscribe.close()
  }
}

export function* subscribeMonthSourcesDataSaga(action) {
  const { responseFailure, responseSuccess } = action

  try {
    const month = yield select(monthSelector)
    const year = yield select(yearSelector)
    const routeId = yield select(routeIdSelector)

    const colRef = yield firestore.collection(`routes/${routeId}/info`)
    const query = colRef
      .where('month', '==', month + 1)
      .where('year', '==', year)

    const channel = yield rsf.firestore.channel(query)

    yield fork(unsubscribeMonthSourcesDataSaga, channel)

    while (true) {
      const res = yield take(channel)

      if (!res.empty) {
        const sources = yield select(sourcesSelector)

        const resData = []

        res.forEach(data => resData.push({ id: data.id, ...data.data() }))

        const days = getDaysInMonth(month, year)

        const monthSourcesData = days.map(day => {
          const date = createReadableDate(day)
          const dateId = createDateId(day)

          const serverData = resData.find(({ id }) => id === dateId)

          if (serverData) {
            const { sourcesData, ...other } = serverData

            const updatedSourcesData = sources.map(data => {
              const serverSource = sourcesData && sourcesData.find(({ id }) => id === data.id)

              return {
                ...data,
                ...serverSource,
              }
            })

            return {
              ...other,
              day: date,
              id: dateId,
              sourcesData: updatedSourcesData
            }
          } else {
            return {
              day: date,
              id: dateId,
              sourcesData: sources
            }
          }
        })

        yield put(responseSuccess(monthSourcesData))
      } else {
        yield put(responseSuccess([]))
      }
    }
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час завантаження даних з систем!'
    }))
  }
}

export function* updateMonthSourcesDataSagas(action) {
  const { responseSuccess, data: { id, key, value, sourceId } } = action

  const monthSourcesData = yield select(monthSourcesDataSelector)

  if (sourceId) {
    const updatedData = monthSourcesData.map(data => {
      if (data.id === id) {
        const sourcesData = data.sourcesData.map(source => {
          if (source.id === sourceId) {
            return {
              ...source,
              [key]: Number(value)
            }
          }

          return source
        })

        return {
          ...data,
          sourcesData
        }
      }

      return data
    })

    yield put(responseSuccess(updatedData))
  } else {
    const updatedData = monthSourcesData.map(data => {
      if (data.id === id) {
        return {
          ...data,
          [key]: value
        }
      }

      return data
    })

    yield put(responseSuccess(updatedData))
  }
}

export function* setMonthSourcesDataSagas(action) {
  const { responseFailure, responseSuccess, data: { id, key, value, sourceId, diff } } = action

  try {
    const routeId = yield select(routeIdSelector)
    const monthSourcesData = yield select(monthSourcesDataSelector)
    const mergedSources = yield select(mergedSourcesSelector)
    const date = createDateFromDateId(id)
    const month = date.getMonth() + 1
    const year = date.getFullYear()

    if (sourceId) {
      const { sourcesData } = monthSourcesData.find(data => data.id === id)

      const updatedSources = sourcesData.map(source => {
        if (source.id === sourceId) {
          return {
            ...source,
            [key]: Number(value)
          }
        }

        return source
      })

      yield call(
        rsf.firestore.setDocument,
        `routes/${routeId}/info/${id}`,
        { 
          month,
          year,
          sourcesData: updatedSources
        },
        { merge: true }
      )

      if (key === 'booked' && diff > 0) {
        const { label } = mergedSources.find(({ id }) => id === sourceId)
        const bookings = yield createSourceBooking(label, sourceId, diff, date)

        const res = yield handleBooking(bookings)

        if (res[0]) {
          yield put(snackbarActions.showSnackbar({
            type: 'success',
            message: 'Дані оновлено'
          }))

          yield put(responseSuccess())
        }
      }
    } else {
      yield call(
        rsf.firestore.setDocument,
        `routes/${routeId}/info/${id}`,
        { 
          month,
          year,
          [key]: value
        },
        { merge: true }
      )

      yield put(snackbarActions.showSnackbar({
        type: 'success',
        message: 'Перевізник змінений'
      }))

      yield put(responseSuccess())
    }
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час запису даних!'
    }))
  }
}



// WEBPACK FOOTER //
// ./src/reduxModules/sagas/SourceSagas.js